// Игра во время матча (§2.18 этап 2в, крикет): под карточкой «Кто победит?» у ИДУЩЕГО матча (в блоке есть `live`).
// Вопрос про следующий овер: ответ — POST over/ ровно с scoreboard и over сервера, человеку — over_number; ответ —
// блок матча целиком (в кэш). Опоздал — 409 over_closed: наш тост и перечитать. Очков рейтинга овер не даёт — reward
// уходит в сундук матча. «Болеем»: тапы копим и шлём пачкой (≤ batch_max) раз в ~1,5 с, до cap за матч; ответ
// {accepted, cheer} заменяет шкалу; 409 cheer_closed — блок гасим. Уходя с экрана — досылаем накопленное.
import { useEffect, useRef, useState } from 'react';
import { sendCheer, useOverAnswer } from '../api/gameQueries';
import type { GameCheer, GameFixture, GameOverQuestion } from '../api/gameTypes';
import { t } from '../i18n/i18n';
import { MEDAL, XP } from '../screens/world/worldArt';
import { haptic } from '../telegram/webapp';
import { addTap, cheerShares, isCheerClosed, liveErrorKey, takeBatch, totalTaps, type Pending } from './liveGameModel';
import { TeamLogo } from './TeamLogo';
import { toast } from './Toast';
import pc from './PredictionCard.module.css';
import styles from './LiveGame.module.css';

const FLUSH_MS = 1500;

export function LiveGame({ fixture, fixtureUrl }: { fixture: GameFixture; fixtureUrl?: string }) {
  const live = fixture.live;
  if (!live) return null;
  return (
    <div className={styles.wrap}>
      <div className={styles.head}><span className={styles.dot} aria-hidden="true" />{t('tg_game_live_title')}</div>
      {live.over_question && <OverQuestion fixture={fixture} q={live.over_question} fixtureUrl={fixtureUrl} />}
      {live.my_overs && live.my_overs.answered > 0 && <MyOvers fixture={fixture} />}
      {live.cheer && <Cheer fixture={fixture} serverCheer={live.cheer} fixtureUrl={fixtureUrl} />}
    </div>
  );
}

function OverQuestion({ fixture, q, fixtureUrl }: { fixture: GameFixture; q: GameOverQuestion; fixtureUrl?: string }) {
  const answer = useOverAnswer(fixture.fixture_id, fixtureUrl);
  const pick = (value: string) => {
    haptic.impact();
    answer.mutate({ question: q, pick: value }, { onError: (e) => toast(t(liveErrorKey(e))) });
  };
  return (
    <div className={styles.block}>
      <div className={styles.qHead}>
        <span className={styles.qTitle}>{q.title}</span>
        <span className={styles.reward}>{[(q.reward.medals ?? 0) > 0 && `+${q.reward.medals} ${MEDAL}`, (q.reward.xp ?? 0) > 0 && `+${q.reward.xp} ${XP}`].filter(Boolean).join('  ')}</span>
      </div>
      <div className={styles.buckets}>
        {q.options.map((o) => {
          const mine = q.my_pick === o.pick;
          return (
            <button key={o.pick} className={`${pc.option} ${styles.bucket} ${mine ? pc.mine : ''}`} aria-pressed={mine}
              disabled={answer.isPending} onClick={() => { if (!mine) pick(o.pick); }}>
              <span className={`score ${styles.bucketTitle}`}>{o.title}</span>
            </button>
          );
        })}
      </div>
      <div className={pc.footer}><span>{q.hint}</span></div>
    </div>
  );
}

function MyOvers({ fixture }: { fixture: GameFixture }) {
  const mo = fixture.live!.my_overs!;
  const titles = Object.fromEntries((fixture.live!.over_question?.options ?? []).map((o) => [o.pick, o.title]));
  const mark = (status: string) => (status === 'won' ? '✓' : status === 'lost' ? '✗' : '—');
  return (
    <div className={styles.block}>
      <div className={styles.summary}>{t('tg_game_overs_summary', mo.won, mo.answered)}</div>
      {mo.last.map((r) => (
        <div key={`${r.scoreboard ?? ''}-${r.over_number}`} className={`${styles.overRow} ${r.status === 'won' ? styles.overWon : ''}`}>
          <span>{t('tg_over_n', r.over_number)}</span>
          <span className="score">{titles[r.pick] ?? r.pick}</span>
          {r.runs !== null && <span className={styles.muted}>{t('tg_game_over_runs', r.runs)}</span>}
          <span className={styles.mark}>{mark(r.status)}</span>
        </div>
      ))}
    </div>
  );
}

function Cheer({ fixture, serverCheer, fixtureUrl }: { fixture: GameFixture; serverCheer: GameCheer; fixtureUrl?: string }) {
  const id = fixture.fixture_id;
  const [answered, setAnswered] = useState<GameCheer | null>(null);
  // свежее из двух: ответ на пачку или перечитанный блок (шкала у сервера кэшируется на refresh_seconds)
  const cheer = answered && answered.my_taps >= serverCheer.my_taps ? answered : serverCheer;
  const [pending, setPending] = useState<Pending>({});
  const [closed, setClosed] = useState(false);
  const [pops, setPops] = useState<Record<string, number>>({});
  const refs = useRef({ cheer, pending, inFlight: 0 });
  refs.current.cheer = cheer;
  refs.current.pending = pending;

  useEffect(() => {
    if (closed) return;
    const flush = () => {
      const r = refs.current;
      if (r.inFlight) return;
      const batch = takeBatch(r.cheer, r.pending);
      if (!batch) return;
      r.inFlight = batch.taps;
      r.pending = batch.rest;
      setPending(batch.rest);
      sendCheer(id, batch.side, batch.taps, fixtureUrl)
        .then((res) => setAnswered(res.cheer))
        .catch((e) => { if (isCheerClosed(e)) setClosed(true); })
        .finally(() => { r.inFlight = 0; });
    };
    const timer = setInterval(flush, FLUSH_MS);
    return () => {
      clearInterval(timer);
      // уходим — досылаем накопленное пачками (ответ уже некому рисовать)
      const r = refs.current;
      let rest = r.pending;
      let base = r.cheer;
      for (let batch = takeBatch(base, rest); batch; batch = takeBatch(base, rest)) {
        void sendCheer(id, batch.side, batch.taps, fixtureUrl).catch(() => {});
        base = { ...base, my_taps: base.my_taps + batch.taps };
        rest = batch.rest;
      }
    };
  }, [id, fixtureUrl, closed]);

  if (closed) return null;
  const mine = totalTaps(cheer, pending, refs.current.inFlight);
  const full = mine >= cheer.cap;
  const shares = cheerShares(cheer);
  const teams: Record<string, GameFixture['home'] | undefined> = { home: fixture.home, away: fixture.away };
  const tap = (side: string) => {
    const next = addTap(cheer, pending, side, refs.current.inFlight);
    if (!next) return;
    haptic.selection();
    setPending(next);
    setPops((p) => ({ ...p, [side]: (p[side] ?? 0) + 1 }));
  };
  return (
    <div className={styles.block}>
      <div className={styles.qHead}><span className={styles.qTitle}>{cheer.title}</span></div>
      <div className={styles.meter} aria-hidden="true">
        {cheer.sides.map((s) => <span key={s.side} className={styles[`meter_${s.side}`] ?? styles.meter_home} style={{ width: `${shares[s.side]}%` }} />)}
      </div>
      <div className={styles.sides}>
        {cheer.sides.map((s) => {
          const team = teams[s.side];
          return (
            <button key={s.side} className={styles.side} disabled={full} onClick={() => tap(s.side)}>
              {team && <TeamLogo src={team.image} code={team.code} size={32} />}
              <span className={styles.sideName}>{team?.name ?? s.side}</span>
              <span className={`score ${styles.sidePct}`}>{s.percent === null ? '—' : `${s.percent}%`}</span>
              {pops[s.side] ? <span key={pops[s.side]} className={styles.pop} aria-hidden="true">+1</span> : null}
            </button>
          );
        })}
      </div>
      <div className={pc.footer}>
        <span>{t('tg_game_cheer_mine', mine, cheer.cap)}</span>
        {cheer.bonus_reached ? <span className={styles.bonus}>✓ {t('tg_game_cheer_bonus')}</span> : <span>{cheer.hint}</span>}
        {full && <span>{t('tg_game_cheer_cap')}</span>}
      </div>
    </div>
  );
}
